/**
 * 主要人员模块
 */

import React, { FC, useEffect, useMemo, useState } from 'react';
import { ProDetailList } from '@bedrock/eed-react';
import { Button, Radio } from '@bedrock/components';
import { ChevronDown, ChevronUp } from '@bedrock/icons-react';
import {
  FormModule,
  FormDescriptions,
  FormItemSupervisorDetailList,
  FormItemSupervisorInputOrNot,
  FormItemSupervisorInputNotReason,
  FormItemPersonName,
  FormItemPersonIdType,
  FormItemPersonId,
  FormItemPositions,
  FormItemAppointmentAt,
  FormItemQccKeyMemberDetailList,
  DataSourceTag,
} from '@purchase/supplier';
import { isEmpty } from 'lodash';
import { useExecutiveRelationModal } from '../../Qcc/useExecutiveRelationModal';

type DataSource = 'supplier' | 'thirdParty';

const KeyPersonnelModule: FC<{
  formDetail: any;
  isExternal?: boolean;
}> = ({ formDetail, isExternal = false }) => {
  const [keyPersonnelSource, setKeyPersonnelSource] =
    useState<DataSource>('supplier');
  const [showHistoryMembers, setShowHistoryMembers] = useState(false);
  const { openModal, modalElement, hasRelationData } = useExecutiveRelationModal(formDetail);

  const vendorSupervisorInfo = formDetail?.vendorSupervisorInfo;
  const hasSupervisorData = !Boolean(vendorSupervisorInfo?.absenceReason);
  const supplierSupervisorCount = useMemo(
    () => vendorSupervisorInfo?.supervisors?.length ?? 0,
    [vendorSupervisorInfo]
  );

  const qccCurrent = formDetail?.vendorQccKeyMemberInfos ?? [];
  const qccHistory = formDetail?.vendorQccHistoryEmployeeInfos ?? [];
  const qccUpdateTime = formDetail?.qccDataFetchedAt;
  const hasQccHistory = qccHistory.length > 0;
  const qccListFieldName = showHistoryMembers
    ? 'vendorQccKeyMemberWithHistoryInfos'
    : 'vendorQccKeyMemberInfos';

  const vendorQccKeyMemberInfosCount = qccCurrent.length;
  const totalSupervisorCount = isExternal
    ? supplierSupervisorCount
    : supplierSupervisorCount + vendorQccKeyMemberInfosCount;

  // 当只有一方有数据时，自动切换到对应的数据源
  useEffect(() => {
    if (supplierSupervisorCount > 0 && vendorQccKeyMemberInfosCount === 0) {
      setKeyPersonnelSource('supplier');
    } else if (
      supplierSupervisorCount === 0 &&
      vendorQccKeyMemberInfosCount > 0
    ) {
      setKeyPersonnelSource('thirdParty');
    }
  }, [supplierSupervisorCount, vendorQccKeyMemberInfosCount]);

  return (
    <>
      <FormModule
        title="主要人员"
        titleCount={totalSupervisorCount}
        proCardProps={{
          expandable: true,
          defaultExpanded: totalSupervisorCount > 0,
        }}
      >
        {!isExternal && (
          <div className="flex items-center justify-between mb-3">
            <Radio.Group
              value={keyPersonnelSource}
              onChange={(e) => setKeyPersonnelSource(e.target.value)}
            >
              <Radio.Button value="supplier">
                供应商录入 ({supplierSupervisorCount})
              </Radio.Button>
              <Radio.Button value="thirdParty">
                企查查获取 ({vendorQccKeyMemberInfosCount})
              </Radio.Button>
            </Radio.Group>
            {keyPersonnelSource === 'thirdParty' && (
              <DataSourceTag source="企查查" updateTime={qccUpdateTime} />
            )}
          </div>
        )}

        {(isExternal || keyPersonnelSource === 'supplier') && (
          <>
            {!hasSupervisorData ? (
              <FormDescriptions
                columns={1}
                labelStyle={{ width: 180 }}
                margin={false}
              >
                <FormItemSupervisorInputOrNot
                  label="是否上传董监高信息"
                  name={['vendorSupervisorInfo', 'inputOrNot']}
                />
                <FormItemSupervisorInputNotReason
                  label="缺失原因"
                  name={['vendorSupervisorInfo', 'absenceReason']}
                />
              </FormDescriptions>
            ) : (
              <FormItemSupervisorDetailList
                name={['vendorSupervisorInfo', 'supervisors']}
                fieldProps={{ scroll: { x: 1120 } }}
                reminderDisabled
              >
                <ProDetailList.Item
                  label="#"
                  name="#"
                  meta={{ columnProps: { width: 50, fixed: true } }}
                >
                  {(value, record, index) => index + 1}
                </ProDetailList.Item>
                <FormItemPersonName
                  label="姓名"
                  name="name"
                  meta={{
                    columnProps: {
                      width: 200,
                      className: 'font-semibold',
                      fixed: true,
                    },
                  }}
                />
                <FormItemPositions name="positions" label="职务" />
                <FormItemAppointmentAt name="appointmentAt" label="任职日期" />
                <FormItemPersonIdType name="personIdType" label="证件类型" />
                <FormItemPersonId name="personId" label="证件号码" />
              </FormItemSupervisorDetailList>
            )}
          </>
        )}

        {!isExternal && keyPersonnelSource === 'thirdParty' && (
          <>
            <FormItemQccKeyMemberDetailList
              name={qccListFieldName}
              fieldProps={{ scroll: { x: 1120 } }}
              onPersonClick={openModal}
              isPersonClickable={hasRelationData}
            />

            {hasQccHistory && (
              <div className="mt-2 text-xs text-color-2">
                <Button
                  type="link"
                  onClick={() => setShowHistoryMembers((prev) => !prev)}
                  suffixIcon={
                    showHistoryMembers ? <ChevronUp /> : <ChevronDown />
                  }
                >
                  {showHistoryMembers ? '收起历史' : '展开历史'}
                </Button>
              </div>
            )}
          </>
        )}
      </FormModule>
      {modalElement}
    </>
  );
};

export default KeyPersonnelModule;
